import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trash2Icon, MinusIcon, PlusIcon, ArrowRightIcon } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useCart } from '../context/CartContext';
export function CartPage() {
  const {
    cart,
    removeFromCart,
    addToCart,
    totalItems
  } = useCart();
  const subtotal = cart.reduce((acc, item) => {
    const price = parseFloat(item.price.replace('$', ''));
    return acc + price * item.quantity;
  }, 0);
  return <Layout data-id="element-279">
      {/* Hero Section */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center overflow-hidden" data-id="element-280">
        <div className="absolute inset-0 z-0" data-id="element-281">
          <img src="https://cdn.magicpatterns.com/uploads/oGsPVovcJJBHoRtkyu749q/kitchen.png" alt="Cart Background" className="w-full h-full object-cover brightness-[0.5]" data-id="element-282" />
          <div className="absolute inset-0 bg-black/20" data-id="element-283" />
        </div>

        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8
      }} className="relative z-10 text-5xl md:text-8xl font-black text-white uppercase tracking-tighter text-center" data-id="element-284">
          Cart
        </motion.h1>
      </section>

      {/* Cart Content */}
      <section className="py-20 px-6 bg-white min-h-[40vh]" data-id="element-285">
        <div className="max-w-7xl mx-auto" data-id="element-286">
          {cart.length === 0 ? <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="bg-[#F5F5F5] border-t-4 border-[#00A3C4] p-12 text-center" data-id="element-287">
              <p className="text-lg text-charcoal/80 mb-8" data-id="element-288">
                Your cart is currently empty.
              </p>
              <Link to="/products" className="inline-block bg-[#6B0D24] text-white px-10 py-4 font-black uppercase tracking-widest text-xs hover:bg-[#5A0B1E] transition-colors" data-id="element-289">
                Return to Store
              </Link>
            </motion.div> : <div className="grid grid-cols-1 lg:grid-cols-3 gap-16" data-id="element-290">
              {/* Items List */}
              <div className="lg:col-span-2 space-y-8" data-id="element-291">
                {cart.map((item, idx) => <motion.div key={item.name} initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: idx * 0.1
            }} className="flex flex-col md:flex-row items-center gap-8 p-6 bg-alabaster border border-charcoal/5 group" data-id="element-292">
                    <div className="w-32 h-32 bg-white p-2 border border-charcoal/5 shrink-0" data-id="element-293">
                      <img src={item.image} alt={item.name} className="w-full h-full object-contain" data-id="element-294" />
                    </div>

                    <div className="flex-1 text-center md:text-left" data-id="element-295">
                      <h3 className="text-xl font-black text-charcoal uppercase tracking-tight mb-2" data-id="element-296">
                        {item.name}
                      </h3>
                      <p className="text-[#6B0D24] font-bold" data-id="element-297">{item.price}</p>
                    </div>

                    <div className="flex items-center gap-4 bg-white border border-charcoal/10 p-2" data-id="element-298">
                      <button onClick={() => removeFromCart(item.name)} className="p-2 hover:text-[#6B0D24] transition-colors" data-id="element-299">
                        <MinusIcon size={16} data-id="element-300" />
                      </button>
                      <span className="w-8 text-center font-bold" data-id="element-301">
                        {item.quantity}
                      </span>
                      <button onClick={() => addToCart(item)} className="p-2 hover:text-[#6B0D24] transition-colors" data-id="element-302">
                        <PlusIcon size={16} data-id="element-303" />
                      </button>
                    </div>

                    <button onClick={() => removeFromCart(item.name)} className="p-3 text-charcoal/40 hover:text-[#6B0D24] transition-colors" data-id="element-304">
                      <Trash2Icon size={20} data-id="element-305" />
                    </button>
                  </motion.div>)}
              </div>

              {/* Summary */}
              <div className="lg:col-span-1" data-id="element-306">
                <div className="bg-alabaster p-8 border-3 border-charcoal sticky top-32" data-id="element-307">
                  <h3 className="text-2xl font-black text-charcoal uppercase tracking-tight mb-8" data-id="element-308">
                    Order Summary
                  </h3>

                  <div className="space-y-4 mb-8" data-id="element-309">
                    <div className="flex justify-between text-charcoal/60 uppercase tracking-widest text-xs font-bold" data-id="element-310">
                      <span data-id="element-311">Subtotal ({totalItems} items)</span>
                      <span data-id="element-312">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-charcoal/60 uppercase tracking-widest text-xs font-bold" data-id="element-313">
                      <span data-id="element-314">Shipping</span>
                      <span className="text-[#6B0D24]" data-id="element-315">
                        Calculated at checkout
                      </span>
                    </div>
                    <div className="pt-4 border-t border-charcoal/10 flex justify-between items-end" data-id="element-316">
                      <span className="text-sm font-black uppercase tracking-widest" data-id="element-317">
                        Total
                      </span>
                      <span className="text-3xl font-black text-[#6B0D24]" data-id="element-318">
                        ${subtotal.toFixed(2)}
                      </span>
                    </div>
                  </div>

                  <button className="w-full bg-[#6B0D24] text-white py-5 font-black uppercase tracking-[0.2em] text-sm hover:bg-[#5A0B1E] transition-all flex items-center justify-center gap-3 group" data-id="element-319">
                    Checkout
                    <ArrowRightIcon size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-320" />
                  </button>
                </div>
              </div>
            </div>}
        </div>
      </section>
    </Layout>;
}